import React from 'react';
import {Typography} from '@mui/material';
import {CheckCircleOutlineOutlined, CircleOutlined} from '@mui/icons-material';
import {Instructions} from './DesignInstructions';
import {DownloadCsvTemplate} from './DownloadCsvTemplate';
import {UploadCsvs} from './UploadCsv';
import {ChipSamplesTables, ChipSamplesTable} from './ChipSamplesTable';

interface ExperimentDesignProps {
    /**
     * RNA Seq or ChIP Seq
     */
    sequencingType: string;
    sequencingReadType: 'paired' | 'singled';
    filesPerSample: number;
    /**
     * Whether the parent has got any CSV content
     */
    gotCsvDesign: boolean;
    parsedSamples: Record<string, string>[];
    parsedFilenames: Record<string, string[]>;
    setParsedSamples: (samples: Record<string, string>[]) => void;
    setSamplesToFilenames: (samplesToFilenames: Record<string, string[]>) => void;
    setCsvError: (error: string) => void;
}

/**
 * Shows instructions to the user for their experimental design CSV file(s).
 * Enables downloading a template.
 * Enables uploading their own CSV file(s).
 */
export const ExperimentDesign = ({
    sequencingType,
    sequencingReadType,
    filesPerSample,
    gotCsvDesign,
    parsedSamples,
    parsedFilenames,
    setParsedSamples,
    setSamplesToFilenames,
    setCsvError
}: ExperimentDesignProps) => {
    return (
        <>
            <Typography variant="headline" size="small">
                {!gotCsvDesign ? (
                    <CircleOutlined sx={{fontSize: '24px', color: 'primary-container', marginRight: '7px'}} />
                ) : (
                    <CheckCircleOutlineOutlined sx={{fontSize: '24px', color: 'primary.main', marginRight: '7px'}} />
                )}
                1. Format your experimental design
            </Typography>
            <Instructions sequencingType={sequencingType} />
            <DownloadCsvTemplate sequencingType={sequencingType} sequencingReadType={sequencingReadType} />
            <UploadCsvs
                sequencingType={sequencingType}
                sequencingReadType={sequencingReadType}
                filesPerSample={filesPerSample}
                setParsedSamples={setParsedSamples}
                setSamplesToFilenames={setSamplesToFilenames}
                setCsvError={setCsvError}
            />
            {sequencingType === 'ChIP Seq' && <ChipSamplesTables samples={parsedSamples as any} samplenameToFilenames={parsedFilenames} />}
            {sequencingType === 'ATAC Seq' && (
                <ChipSamplesTable
                    samples={parsedSamples as any}
                    samplenameToFilenames={parsedFilenames}
                    controlColumn={false}
                    sampleTypeColumn={false}
                />
            )}
        </>
    );
};
